/**
 * 商品一覧。
 *
 * 並び順と絞り込みは、**スキーマにある範囲ではなく API が実際に honor する範囲**
 * だけを送る（src/api-capabilities.ts）。カバレッジの集計がまだデータ源から
 * 引けないので、それに依存する軸は API が BAD_USER_INPUT で断る。選べてしまうと
 * 一覧を開いた瞬間にエラーになるため、画面側でも選べないようにしてある。
 *
 * ページングは after / endCursor で、「もっと読む」を押すと続きを足す。
 */

import { useState } from "react";
import { Link } from "react-router";
import { useQuery, type CombinedError } from "urql";
import {
  Button,
  Card,
  Disclosure,
  EmptyState,
  ErrorState,
  LoadingState,
  Meter,
  PageHeader,
  ProductsDocument,
  ProductThumbnail,
  Tag,
  type CoverageBand,
  type ProductOrder,
  type PopulationId,
  type ProductsQuery,
  type PublishState,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";

import {
  COVERAGE_BAND_LABEL,
  DEFAULT_PRODUCT_ORDER,
  isSupportedProductOrder,
  NOT_CONNECTED_NOTE,
  PRODUCT_ORDER_LABEL,
} from "../api-capabilities";

import { ProductSearchField, ProductLoadMore } from "./ProductSearchControls";

import { UgcPopulationSelect } from "./UgcPopulationSelect";
import {
  AnswerableFilterFields,
  toAnswerableFilter,
  type AnswerableChoice,
} from "./AnswerableFilterFields";
import { answerabilityText } from "./answerability-text";

const PAGE_SIZE = 20;

const PUBLISH_LABEL: Record<PublishState, string> = {
  PUBLISHED: "配信中",
  STOPPED: "停止中",
  UNSET: "未設定",
};

const PUBLISH_TONE: Record<PublishState, "ok" | "warn" | "neutral"> = {
  PUBLISHED: "ok",
  STOPPED: "warn",
  UNSET: "neutral",
};

// 並び順は全部並べるが、API が honor しないものは選べないようにする
// （選択肢ごと消すと「無い」のか「まだ使えない」のかが分からなくなる）
const ORDERS: ProductOrder[] = [
  "NAME_ASC",
  "COVERAGE_ASC",
  "COVERAGE_DESC",
  "FAQ_DELIVERED_DESC",
];

// 語彙の充足での絞り込みは、集計そのものが無いので丸ごと使えない
const BANDS: CoverageBand[] = ["LOW", "MIDDLE", "HIGH"];

type Edge = ProductsQuery["products"]["edges"][number];

/**
 * API が「その条件はまだ受けられない」と言ってきたとき。
 * 読み込み失敗と混ぜず、条件の問題として出す。
 */
function isUnsupportedCondition(error: CombinedError): boolean {
  return error.graphQLErrors.some(
    (graphQLError) => graphQLError.extensions?.["code"] === "BAD_USER_INPUT",
  );
}

export function ProductsPage() {
  const [population, setPopulation] = useState<PopulationId>("DELIVERED");
  const [minUgcCount, setMinUgcCount] = useState(50);
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [order, setOrder] = useState<ProductOrder>(DEFAULT_PRODUCT_ORDER);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<Edge[]>([]);
  const [answerable, setAnswerable] = useState<AnswerableChoice>("");
  const [answerablePersona, setAnswerablePersona] = useState("");

  const answerableFilter = toAnswerableFilter(answerable, answerablePersona);
  const filter = {
    ...(submittedQuery === "" ? {} : { query: submittedQuery }),
    ...(answerableFilter === null ? {} : { answerable: answerableFilter }),
  };

  const [result, refetch] = useQuery({
    query: ProductsDocument,
    variables: {
      first: PAGE_SIZE,
      population,
      minUgcCount: population === "WITH_UGC" ? minUgcCount : null,
      after: cursor,
      order,
      // honor されないキー（coverageBand / category / publish / hasDescription）は
      // 値が入っていても送らない。絞り込みが何も無ければ filter ごと送らない
      filter: Object.keys(filter).length === 0 ? null : filter,
    },
  });

  const connection = result.data?.products;
  // 最初のページは result から、続きは「もっと読む」で足したぶんを前に置く
  // 次ページの取得中は urql が前ページの data を保持する。同じ商品を二重に
  // 描画せず、応答が更新されたら最新の node で置き換える。
  const edges = Array.from(
    new Map(
      [...loaded, ...(connection?.edges ?? [])].map((edge) => [
        edge.node.id,
        edge,
      ]),
    ).values(),
  );

  const resetPaging = (): void => {
    setLoaded([]);
    setCursor(null);
  };

  return (
    <div className="vd-page">
      <PageHeader
        title="商品"
        description="商品ごとに、答えを作るための語彙がどれだけ埋まっているかを見ます。"
        titleExtra={<HelpIcon slug="products/list" />}
      />

      <div className="vd-stack">
        <Card
          title="絞り込み"
          extra={<HelpIcon slug="products/list" anchor="絞り込み" />}
        >
          <UgcPopulationSelect
            population={population}
            minimum={minUgcCount}
            onChange={(next, minimum) => {
              setPopulation(next);
              setMinUgcCount(minimum);
              resetPaging();
            }}
          />
          {/* 母数の隣に置く。判定に使う集計は、選んでいる母数のもの */}
          <div style={{ marginTop: 12 }}>
            <AnswerableFilterFields
              idPrefix="products"
              choice={answerable}
              personaId={answerablePersona}
              onChange={(nextChoice, nextPersona) => {
                setAnswerable(nextChoice);
                setAnswerablePersona(nextPersona);
                resetPaging();
              }}
            />
          </div>
          {population === "WITH_UGC" &&
          !result.fetching &&
          !result.error &&
          connection?.totalCount != null ? (
            <p className="vd-stat-note">
              UGC {minUgcCount} 件以上で絞った母数:{" "}
              {connection.totalCount.toLocaleString("ja-JP")}{" "}
              商品（検索条件を指定した場合はその一致件数）
            </p>
          ) : null}
          <form
            className="vd-row"
            onSubmit={(event) => {
              event.preventDefault();
              setSubmittedQuery(query.trim());
              resetPaging();
            }}
          >
            <ProductSearchField value={query} onChange={setQuery} />
            <label className="vd-field">
              <span className="vd-field-hint">語彙の充足</span>
              <select className="vd-select" value="" disabled>
                <option value="">すべて</option>
                {BANDS.map((value) => (
                  <option key={value} value={value} disabled>
                    {COVERAGE_BAND_LABEL[value]}
                  </option>
                ))}
              </select>
            </label>
            <label className="vd-field">
              <span className="vd-field-hint">並び順</span>
              <select
                className="vd-select"
                value={order}
                onChange={(event) => {
                  setOrder(event.target.value as ProductOrder);
                  resetPaging();
                }}
              >
                {ORDERS.map((value) => (
                  <option
                    key={value}
                    value={value}
                    disabled={!isSupportedProductOrder(value)}
                  >
                    {PRODUCT_ORDER_LABEL[value]}
                    {isSupportedProductOrder(value)
                      ? ""
                      : `（${NOT_CONNECTED_NOTE}）`}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type="submit"
              variant="primary"
              style={{ alignSelf: "flex-end" }}
            >
              絞り込む
            </Button>
          </form>
          <div style={{ marginTop: 12 }}>
            <Disclosure summary="選べない並び順と絞り込みがあるのはなぜか">
              <p style={{ margin: 0 }}>
                語彙の充足（カバレッジ）の集計が、まだデータ源から引けていません。
                充足を見る並び順と絞り込みはその集計に載っているので、
                {NOT_CONNECTED_NOTE}。
              </p>
              <p style={{ margin: "4px 0 0" }}>
                いま使えるのは、キーワードでの絞り込みと名前順の並びです。
              </p>
            </Disclosure>
          </div>
        </Card>

        {result.error !== undefined ? (
          isUnsupportedCondition(result.error) ? (
            <ErrorState
              title="この条件はまだ使えません"
              description={`${NOT_CONNECTED_NOTE}。キーワードでの絞り込みと名前順の並びに戻すと表示できます。`}
              onRetry={() => {
                setOrder(DEFAULT_PRODUCT_ORDER);
                resetPaging();
              }}
              retryLabel="使える条件に戻す"
            />
          ) : (
            <ErrorState
              description={result.error.message}
              onRetry={() => refetch({ requestPolicy: "network-only" })}
            />
          )
        ) : result.fetching && edges.length === 0 ? (
          <LoadingState />
        ) : edges.length === 0 ? (
          <EmptyState
            title="条件に合う商品がありません"
            description="キーワードや絞り込みを外すと見つかるかもしれません。"
          />
        ) : (
          <Card
            title="商品一覧"
            extra={
              <span className="vd-stat-note">
                {connection?.totalCount == null
                  ? `${edges.length} 件を表示`
                  : `全 ${connection.totalCount.toLocaleString("ja-JP")} 件のうち ${edges.length} 件を表示`}
              </span>
            }
          >
            <div className="vd-table-scroll">
              <table className="vd-table">
                <thead>
                  <tr>
                    <th>商品</th>
                    <th>カテゴリ</th>
                    <th className="vd-num">語彙の充足</th>
                    <th className="vd-num">配信している答え</th>
                    <th className="vd-num">答えられる問い</th>
                    <th>配信</th>
                  </tr>
                </thead>
                <tbody>
                  {edges.map(({ node }) => (
                    <tr key={node.id}>
                      <td>
                        {/* 画像は名前の左に小さく置く（デモの見せ方）。
                            取り込めていない商品は空の四角でなく「画像なし」 */}
                        <div className="vd-row" style={{ gap: 12 }}>
                          <ProductThumbnail
                            imageUrl={node.imageUrl ?? null}
                            name={node.name ?? null}
                          />
                          <div style={{ minWidth: 0 }}>
                            <Link to={`/products/${node.id}`}>
                              {node.name ?? "(名前が入っていません)"}
                            </Link>
                            {/* sku はデータ源に列が無いことがある。無いときは
                                行を作らない（空の行を残すと「空欄がある」と読める） */}
                            {node.sku == null || node.sku === "" ? null : (
                              <div className="vd-stat-note">{node.sku}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{node.category ?? "—"}</td>
                      <td className="vd-num" style={{ minWidth: 140 }}>
                        <div>
                          {node.aio.coverage == null
                            ? "—"
                            : `${Math.round(node.aio.coverage * 100)} %`}
                        </div>
                        <Meter ratio={node.aio.coverage ?? null} />
                        <div className="vd-stat-note">
                          {node.aio.axesFilled} / {node.aio.axesTotal} 軸
                        </div>
                      </td>
                      <td className="vd-num">
                        {node.aio.faqDelivered} / {node.aio.faqTotal}
                      </td>
                      <td className="vd-num">
                        {/* null は「数えていません」。0 件とは書かない */}
                        <span className="vd-stat-note">
                          {answerabilityText(node.answerability)}
                        </span>
                      </td>
                      <td>
                        <Tag tone={PUBLISH_TONE[node.aio.publish]}>
                          {PUBLISH_LABEL[node.aio.publish]}
                        </Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ProductLoadMore
              hasNextPage={connection?.pageInfo.hasNextPage === true}
              fetching={result.fetching}
              onLoad={() => {
                const endCursor = connection?.pageInfo.endCursor;
                if (endCursor == null) return;
                setLoaded(edges);
                setCursor(endCursor);
              }}
            />
          </Card>
        )}
      </div>
    </div>
  );
}
